import type { ComponentProps } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { FadeView, LabelText, LoadingPlaceholder, theme } from '@sg/garnish';

import { CreditCardFormLineItem } from './CreditCardFormLineItem';

/**
 * Placeholder component for credit card form loading state.
 */
export const CreditCardFormLoadingState = (props: CreditCardFormLoadingStateProps) => {
  return (
    <FadeView {...props} style={[styles.container, props.style]}>
      <CardNumber />

      <CreditCardFormLineItem style={styles.rowWithFlex}>
        <ExpirationDate />

        <CVV />
      </CreditCardFormLineItem>

      <PostalCode />
    </FadeView>
  );
};

const CardNumber = () => {
  const { formatMessage } = useIntl();

  return (
    <CreditCardFormLineItem style={styles.column}>
      <View style={styles.column}>
        <LabelText sizeMatch={['12']}>{formatMessage(messages.cardNumber)}</LabelText>

        <LoadingPlaceholder rows={1} rowHeight={52} palette="cream" />
      </View>
    </CreditCardFormLineItem>
  );
};

const ExpirationDate = () => {
  const { formatMessage } = useIntl();

  return (
    <View style={[styles.column, styles.marginRight]}>
      <LabelText sizeMatch={['12']}>{formatMessage(messages.expirationDate)}</LabelText>

      <LoadingPlaceholder rows={1} rowHeight={52} palette="cream" />
    </View>
  );
};

const CVV = () => {
  const { formatMessage } = useIntl();

  return (
    <View style={[styles.column, styles.marginLeft]}>
      <LabelText sizeMatch={['12']}>{formatMessage(messages.cvv)}</LabelText>

      <LoadingPlaceholder rows={1} rowHeight={52} palette="cream" />
    </View>
  );
};

const PostalCode = () => {
  const { formatMessage } = useIntl();

  return (
    <CreditCardFormLineItem style={styles.column}>
      <LabelText sizeMatch={['12']}>{formatMessage(messages.zipCode)}</LabelText>

      <LoadingPlaceholder rows={1} rowHeight={52} palette="cream" />
    </CreditCardFormLineItem>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    zIndex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    ...StyleSheet.absoluteFillObject,
  },
  rowWithFlex: {
    flex: 1,
    flexDirection: 'row',
  },
  column: {
    flex: 1,
    gap: theme.spacing['1'],
  },
  marginRight: {
    marginRight: theme.spacing['3'],
  },
  marginLeft: {
    marginLeft: theme.spacing['3'],
  },
});

// ─── Types ─────────────────────────────────────────────────────────────

type CreditCardFormLoadingStateProps = ComponentProps<typeof FadeView>;

// ─── Messages ──────────────────────────────────────────────────────────

const messages = defineMessages({
  cardNumber: {
    defaultMessage: 'Card number',
    description: 'Credit Card Form Loading State | Card number label',
  },
  expirationDate: {
    defaultMessage: 'Expiration date',
    description: 'Credit Card Form Loading State | Expiration date label',
  },
  cvv: {
    defaultMessage: 'CVV',
    description: 'Credit Card Form Loading State | CVV label',
  },
  zipCode: {
    defaultMessage: 'ZIP code',
    description: 'Credit Card Form Loading State | ZIP code label',
  },
});
